/*
 * Suzanoh voice-call bridge (SD-211) — the shared transport/state layer.
 *
 * One source, two consumers: widget.js.j2 inlines this file via Jinja
 * include (single-file widget contract), chat-app bundles it via a
 * side-effect import. It therefore must stay framework-free, define only
 * window.SuzanohVoiceCall, and contain no Jinja delimiters (no double
 * opening braces anywhere, including template literals).
 *
 * Responsibilities (design §6.1): mic capture (AudioWorklet, ScriptProcessor
 * fallback; Float32 -> Int16 16kHz), 24kHz playback queue with barge-in
 * flush, WS + first-frame auth, ringtone, mic-drift telemetry, and the
 * agent-bubble reset anchor (§6.2) so both UIs stay dumb renderers.
 * UI, /voice/start calls, and i18n belong to each surface.
 */
(function (global) {
  'use strict';

  var SEND_RATE = 16000;
  var RECV_RATE = 24000;
  var DRIFT_INTERVAL_MS = 30000;

  // Level meter smoothing, per animation frame. Asymmetric on purpose: a
  // syllable's attack should show up immediately, but the decay has to outlast
  // the gaps between syllables or the bars flicker through a sentence.
  // Tuned by eye at 60Hz; a 120Hz display decays about twice as fast, which
  // still reads as a voice, so the frame rate is not compensated for.
  var LEVEL_ATTACK = 0.5;
  var LEVEL_RELEASE = 0.18;
  // Exponential decay only approaches zero, so the bars would rest just above
  // the floor forever. Below this the meter is snapped shut.
  var LEVEL_SILENCE = 0.02;
  // 1024 samples = ~43ms at RECV_RATE, about one syllable's worth. 256 was a
  // 10ms window, short enough that the reading tracked the waveform itself.
  var LEVEL_FFT_SIZE = 1024;

  /**
   * Stateful linear-interpolation resampler, inRate -> outRate, Float32 in and
   * Int16 out. Carries the fractional read position and the last input sample
   * across calls so block boundaries do not click. Linear interpolation has no
   * anti-alias filter; speech through 48k -> 16k is intelligible for ASR, which
   * is the only consumer.
   *
   * ES5 on purpose: it is also .toString()'d into the worklet source.
   */
  function makeDownsampler(inRate, outRate) {
    var ratio = inRate / outRate;
    var pos = 0;    // read position in the current block; -1 <= pos < 0 points into `last`
    var last = 0;   // final sample of the previous block
    return function (f32) {
      var n = f32.length;
      if (ratio === 1) {
        var same = new Int16Array(n);
        for (var k = 0; k < n; k++) {
          var c = Math.max(-1, Math.min(1, f32[k]));
          same[k] = Math.round(c * 32767);
        }
        return same;
      }
      var out = new Int16Array(Math.ceil((n + 1) / ratio) + 1);
      var count = 0;
      // Stop when the interpolation partner would be in the next block.
      while (pos + 1 < n) {
        var i = Math.floor(pos);
        var frac = pos - i;
        var s0 = i < 0 ? last : f32[i];
        var s1 = f32[i + 1];
        var v = s0 + (s1 - s0) * frac;
        v = Math.max(-1, Math.min(1, v));
        out[count++] = Math.round(v * 32767);
        pos += ratio;
      }
      pos -= n;
      last = f32[n - 1];
      return out.subarray(0, count);
    };
  }

  // AudioWorklet processor, loaded from a Blob URL so the bridge stays a
  // single file. The context runs at the device's native rate (Firefox
  // refuses a mic source whose rate differs from the context's), so the
  // worklet resamples to SEND_RATE itself and posts ~16ms Int16 PCM frames.
  var WORKLET_SOURCE = [
    'const downsample = (' + makeDownsampler.toString() + ')(sampleRate, ' + SEND_RATE + ');',
    'const FRAME = ' + (SEND_RATE / 1000) * 16 + ';',
    'class SuzanohCapture extends AudioWorkletProcessor {',
    '  constructor() { super(); this.pending = []; this.pendingLen = 0; }',
    '  process(inputs) {',
    '    const ch = inputs[0] && inputs[0][0];',
    '    if (ch && ch.length) {',
    '      const part = downsample(ch);',
    '      if (part.length) { this.pending.push(part); this.pendingLen += part.length; }',
    '      if (this.pendingLen >= FRAME) {',
    '        const out = new Int16Array(this.pendingLen);',
    '        let off = 0;',
    '        for (const p of this.pending) { out.set(p, off); off += p.length; }',
    '        this.pending = []; this.pendingLen = 0;',
    '        this.port.postMessage(out.buffer, [out.buffer]);',
    '      }',
    '    }',
    '    return true;',
    '  }',
    '}',
    "registerProcessor('suzanoh-capture', SuzanohCapture);",
  ].join('\n');

  function noop() {}

  function cb(callbacks, name) {
    return typeof callbacks[name] === 'function' ? callbacks[name] : noop;
  }

  /**
   * Resolve the WS endpoint from a /voice/start response.
   * ws_url empty = single-process local mode: same host as the API base.
   */
  function resolveWsUrl(wsUrl, apiBaseUrl) {
    if (wsUrl) return wsUrl;
    var u = new URL(apiBaseUrl, global.location && global.location.href);
    var scheme = u.protocol === 'https:' ? 'wss:' : 'ws:';
    return scheme + '//' + u.host + '/voice/ws';
  }

  function create(options) {
    var callbacks = (options && options.callbacks) || {};
    var onConnecting = cb(callbacks, 'onConnecting');
    var onSessionStart = cb(callbacks, 'onSessionStart');
    var onAgentText = cb(callbacks, 'onAgentText');
    var onUserTranscript = cb(callbacks, 'onUserTranscript');
    var onCandidates = cb(callbacks, 'onCandidates');
    var onInterrupted = cb(callbacks, 'onInterrupted');
    var onLevel = cb(callbacks, 'onLevel');
    var onEndingSoon = cb(callbacks, 'onEndingSoon');
    var onEnded = cb(callbacks, 'onEnded');
    var onError = cb(callbacks, 'onError');

    var ws = null;
    var micCtx = null;
    var playCtx = null;
    var stream = null;
    var captureNode = null;
    var sourceNode = null;
    var playHead = 0;
    var activeSources = [];
    var analyser = null;
    var levelData = null;
    var levelRaf = null;
    var lastLevel = -1;
    var smoothedLevel = 0;
    var muted = false;
    var active = false;
    var endedReason = null;
    var agentBubbleClosed = false; // §6.2: reset anchor for the fixed bubble
    var driftTimer = null;
    var audioSecondsSent = 0;
    var captureStartedMs = 0;
    var ringOsc = null;

    function emitEnded(reason) {
      if (endedReason !== null) return;
      endedReason = reason;
      teardown();
      onEnded(reason);
    }

    function fail(message) {
      onError(message);
      emitEnded('error');
    }

    /* ---- ringtone (connecting): one short ring, prototype 440/480Hz ---- */

    function startRing() {
      if (!playCtx) return;
      try {
        var gain = playCtx.createGain();
        gain.gain.value = 0.06;
        gain.connect(playCtx.destination);
        var oscA = playCtx.createOscillator();
        var oscB = playCtx.createOscillator();
        oscA.frequency.value = 440;
        oscB.frequency.value = 480;
        oscA.connect(gain);
        oscB.connect(gain);
        var t = playCtx.currentTime;
        oscA.start(t);
        oscB.start(t);
        oscA.stop(t + 1.6);
        oscB.stop(t + 1.6);
        ringOsc = { gain: gain, oscA: oscA, oscB: oscB };
      } catch (e) { /* cosmetic only */ }
    }

    function stopRing() {
      if (!ringOsc) return;
      try {
        ringOsc.oscA.stop();
        ringOsc.oscB.stop();
        ringOsc.gain.disconnect();
      } catch (e) { /* already stopped */ }
      ringOsc = null;
    }

    /* ---- level meter: measured at the speaker, not on arrival ---- */

    /*
     * The waveform reads the signal on its way out of playCtx through an
     * AnalyserNode, polled on rAF. Measuring a chunk when it *arrives* runs
     * ahead of the sound by however much audio is queued (playHead -
     * currentTime), and Gemini sends in bursts faster than real time, so that
     * was 1-2 seconds off. Reading the output cannot drift: what the analyser
     * sees is what the speaker is playing, and silence reads as silence
     * without needing an onended hook to notice the queue drained.
     */

    function emitLevel(value) {
      // Quantised so a steady tone stops re-notifying the UI every frame.
      var level = Math.round(value * 100) / 100;
      if (level === lastLevel) return;
      lastLevel = level;
      onLevel(level);
    }

    function readLevel() {
      if (!analyser || endedReason !== null) {
        // Clear the handle too: leaving a fired one behind would make
        // startLevelLoop refuse to start and kill the meter for the rest of
        // the call. Not reachable today, but one guard away from being.
        levelRaf = null;
        return;
      }
      analyser.getFloatTimeDomainData(levelData);
      var sum = 0;
      for (var i = 0; i < levelData.length; i++) sum += levelData[i] * levelData[i];
      var raw = Math.min(1, Math.sqrt(sum / levelData.length) * 4);
      // Speech amplitude swings hard inside one window, so the bars would
      // twitch if the reading were used as-is. Rise fast enough to catch the
      // attack of a syllable, fall slowly enough to read as a voice.
      var weight = raw > smoothedLevel ? LEVEL_ATTACK : LEVEL_RELEASE;
      smoothedLevel = smoothedLevel + (raw - smoothedLevel) * weight;
      if (raw < LEVEL_SILENCE && smoothedLevel < LEVEL_SILENCE) smoothedLevel = 0;
      emitLevel(smoothedLevel);
      if (activeSources.length === 0 && smoothedLevel === 0) {
        // Nothing scheduled and the meter has finished falling: stop spinning
        // until the next chunk arrives (playChunk restarts the loop). The
        // release above is what makes this safe to do — the loop only stops
        // once the bars are already down, never mid-decay.
        stopLevelLoop();
        return;
      }
      levelRaf = requestAnimationFrame(readLevel);
    }

    function startLevelLoop() {
      // rAF is paused while the tab is in the background; the meter freezes at
      // its last value, which nobody is looking at, and the first frame after
      // the tab comes back corrects it.
      if (levelRaf !== null || !analyser) return;
      levelRaf = requestAnimationFrame(readLevel);
    }

    function stopLevelLoop() {
      if (levelRaf !== null) {
        cancelAnimationFrame(levelRaf);
        levelRaf = null;
      }
      smoothedLevel = 0;
      emitLevel(0);
    }

    /* ---- playback: scheduled buffers, flushed on barge-in ---- */

    function playChunk(buf) {
      if (!playCtx) return;
      var i16 = new Int16Array(buf);
      var f32 = new Float32Array(i16.length);
      for (var i = 0; i < i16.length; i++) f32[i] = i16[i] / 32768;
      var audio = playCtx.createBuffer(1, f32.length, RECV_RATE);
      audio.copyToChannel(f32, 0);
      var src = playCtx.createBufferSource();
      src.buffer = audio;
      src.connect(analyser || playCtx.destination);
      var at = Math.max(playCtx.currentTime + 0.01, playHead);
      src.start(at);
      playHead = at + audio.duration;
      activeSources.push(src);
      src.onended = function () {
        activeSources = activeSources.filter(function (s) { return s !== src; });
      };
      startLevelLoop();
    }

    function flushPlayback() {
      activeSources.forEach(function (src) {
        try { src.stop(); } catch (e) { /* already ended */ }
      });
      activeSources = [];
      if (playCtx) playHead = playCtx.currentTime;
      stopLevelLoop();
    }

    /* ---- capture: AudioWorklet, ScriptProcessor fallback ---- */

    function handleFrame(buffer) {
      // Frames before session_start are meaningless (the agent greets first)
      // and would eat into the server's real-time audio allowance while the
      // engine is still connecting — dropped speech right after pickup.
      if (!active || muted || !ws || ws.readyState !== WebSocket.OPEN) return;
      audioSecondsSent += buffer.byteLength / 2 / SEND_RATE;
      ws.send(buffer);
    }

    function startCapture() {
      return navigator.mediaDevices
        .getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } })
        .then(function (s) {
          if (endedReason !== null) {
            // The call died while the permission prompt was up (refused
            // auth, busy, ...): teardown already ran, so release the mic
            // here or the recording indicator stays on until reload.
            s.getTracks().forEach(function (t) { t.stop(); });
            return;
          }
          stream = s;
          // Native rate, not SEND_RATE: Firefox rejects a mic source into a
          // context of another rate (NotSupportedError), and engines that
          // ignore the option would hand us 48k frames labelled as 16k. The
          // capture path resamples itself; mic_context_rate reports the truth.
          // Normally already created by prime() inside the tap's gesture.
          if (!micCtx) micCtx = new AudioContext();
          sendMetric({
            name: 'mic_context_rate',
            value: micCtx.sampleRate,
            track_rate:
              (s.getAudioTracks()[0] &&
                s.getAudioTracks()[0].getSettings &&
                s.getAudioTracks()[0].getSettings().sampleRate) || null,
          });
          sourceNode = micCtx.createMediaStreamSource(s);
          var workletUrl = URL.createObjectURL(
            new Blob([WORKLET_SOURCE], { type: 'application/javascript' })
          );
          return micCtx.audioWorklet
            .addModule(workletUrl)
            .then(function () {
              captureNode = new AudioWorkletNode(micCtx, 'suzanoh-capture');
              captureNode.port.onmessage = function (ev) { handleFrame(ev.data); };
              sourceNode.connect(captureNode);
            })
            .catch(function () {
              // Older browsers / strict CSP on the host page: fall back to
              // the (deprecated but universal) ScriptProcessor path.
              var downsample = makeDownsampler(micCtx.sampleRate, SEND_RATE);
              var proc = micCtx.createScriptProcessor(512, 1, 1);
              proc.onaudioprocess = function (ev) {
                var i16 = downsample(ev.inputBuffer.getChannelData(0));
                if (i16.length) handleFrame(i16.buffer.slice(0, i16.length * 2));
              };
              sourceNode.connect(proc);
              proc.connect(micCtx.destination); // required for onaudioprocess; silent
              captureNode = proc;
            })
            .then(function () {
              captureStartedMs = Date.now();
              driftTimer = setInterval(function () {
                var wallSec = (Date.now() - captureStartedMs) / 1000;
                if (wallSec > 0) {
                  sendMetric({
                    name: 'audio_wallclock_ratio',
                    value: audioSecondsSent / wallSec,
                    muted: muted,
                  });
                }
              }, DRIFT_INTERVAL_MS);
            });
        });
    }

    function releaseMic() {
      if (driftTimer) { clearInterval(driftTimer); driftTimer = null; }
      if (captureNode) {
        try { captureNode.disconnect(); } catch (e) { /* detached */ }
        captureNode = null;
      }
      if (sourceNode) {
        try { sourceNode.disconnect(); } catch (e) { /* detached */ }
        sourceNode = null;
      }
      if (stream) {
        stream.getTracks().forEach(function (t) { t.stop(); });
        stream = null;
      }
      if (micCtx) { micCtx.close().catch(noop); micCtx = null; }
    }

    function teardown() {
      active = false;
      stopRing();
      releaseMic();
      flushPlayback();
      analyser = null;
      levelData = null;
      if (playCtx) { playCtx.close().catch(noop); playCtx = null; }
      if (ws && ws.readyState !== WebSocket.CLOSED) {
        try { ws.close(); } catch (e) { /* already closing */ }
      }
      ws = null;
    }

    function sendJson(payload) {
      if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(payload));
    }

    function sendMetric(fields) {
      var frame = { type: 'metric' };
      Object.keys(fields).forEach(function (k) { frame[k] = fields[k]; });
      sendJson(frame);
    }

    /* ---- server frames ---- */

    function handleControl(msg, startResolve, startReject) {
      switch (msg.type) {
        case 'session_start':
          active = true;
          // Gesture-strict engines (Safari/iOS) may have left a context
          // suspended; a call that opens silent is worse than a late resume.
          if (playCtx) playCtx.resume().catch(noop);
          if (micCtx) micCtx.resume().catch(noop);
          stopRing();
          onSessionStart(msg.node_id || null);
          startResolve();
          break;
        case 'transcript':
          if (msg.role === 'agent') {
            var reset = agentBubbleClosed;
            agentBubbleClosed = false;
            onAgentText(msg.text, reset);
          } else if (msg.role === 'user') {
            onUserTranscript(msg.text);
          }
          break;
        case 'transcript_turn':
          // The closed turn is the storage anchor server-side; for the UI it
          // only marks that the NEXT agent fragment starts a fresh bubble.
          if (msg.role === 'agent') agentBubbleClosed = true;
          break;
        case 'reply_candidates':
          onCandidates(msg.turn_id, msg.candidates || []);
          break;
        case 'interrupted':
          flushPlayback();
          agentBubbleClosed = true; // the cut-off utterance ends here
          onInterrupted();
          break;
        case 'call_ending_soon':
          onEndingSoon(msg.seconds_left);
          break;
        case 'call_ended':
          emitEnded(msg.reason || 'end');
          break;
        case 'error':
          onError(msg.message || 'voice error');
          if (!active) startReject(new Error(msg.message || 'refused'));
          break;
        default:
          break;
      }
    }

    /* ---- public API ---- */

    var api = {
      /**
       * Open the call. cfg = token (from POST /voice/start), and either
       * wsUrl (the start response's ws_url) or apiBaseUrl to derive it.
       * Resolves on session_start; rejects if the call never opens.
       */
      start: function (cfg) {
        // Single-use: endedReason latches, so a bridge that ended once can
        // never fire onEnded again. Both consumers create one per call.
        if (ws || endedReason !== null) {
          return Promise.reject(new Error('bridge is single-use; create a new one'));
        }
        onConnecting();
        // Consumers that reach start() only after a network round trip are
        // outside the tap's gesture by now; prime() is idempotent, so this
        // covers the ones that did not call it in the handler.
        api.prime();
        playHead = 0;
        if (cfg.ring !== false) startRing();

        return new Promise(function (resolve, reject) {
          var settled = false;
          function resolveOnce() {
            if (!settled) { settled = true; resolve(); }
          }
          function rejectOnce(err) {
            if (!settled) { settled = true; reject(err); }
          }
          var url;
          try {
            url = resolveWsUrl(cfg.wsUrl, cfg.apiBaseUrl);
          } catch (e) {
            rejectOnce(e);
            emitEnded('error');
            return;
          }
          ws = new WebSocket(url);
          ws.binaryType = 'arraybuffer';
          ws.onopen = function () {
            sendJson({ type: 'auth', token: cfg.token });
          };
          ws.onmessage = function (e) {
            if (typeof e.data === 'string') {
              var msg;
              try { msg = JSON.parse(e.data); } catch (err) { return; }
              handleControl(msg, resolveOnce, rejectOnce);
            } else {
              playChunk(e.data);
            }
          };
          ws.onerror = function () {
            rejectOnce(new Error('connection failed'));
            fail('connection failed');
          };
          ws.onclose = function () {
            rejectOnce(new Error('closed'));
            emitEnded('disconnected');
          };

          startCapture().catch(function (err) {
            // Mic permission refused / no device: end the call server-side
            // too (the socket close finalizes it there).
            rejectOnce(err);
            onError(err && err.message ? err.message : 'microphone unavailable');
            emitEnded('mic_denied');
          });
        });
      },

      /**
       * Create (and resume) both AudioContexts. Call this synchronously inside
       * the tap handler, before any await/fetch: Safari and iOS only honour
       * resume() for a context created within the user gesture, and a context
       * created after the /voice/start round trip stays suspended — ringtone
       * and agent audio silent, mic frames never produced, no error anywhere.
       * Idempotent; start() calls it too as a fallback.
       */
      prime: function () {
        if (endedReason !== null) return;
        try {
          if (!playCtx) playCtx = new AudioContext({ sampleRate: RECV_RATE });
          if (!analyser) {
            // Agent audio goes through here on its way to the speaker. The
            // ringtone stays wired straight to destination: it is our own
            // sound, not the agent's, and must not move the waveform.
            analyser = playCtx.createAnalyser();
            analyser.fftSize = LEVEL_FFT_SIZE;
            levelData = new Float32Array(analyser.fftSize);
            analyser.connect(playCtx.destination);
          }
          playCtx.resume().catch(noop);
          if (!micCtx) micCtx = new AudioContext();
          micCtx.resume().catch(noop);
        } catch (e) { /* no Web Audio: start() will surface the failure */ }
      },

      /** Ask the server to end; call_ended comes back before the close. */
      end: function () {
        if (endedReason !== null) return;
        // Before session_start the socket may not be open, and sendJson is a
        // no-op then — the tap would be lost while /voice/start keeps going,
        // so the call would go live and bill anyway. Tear down locally
        // instead: the latched reason makes a late start() resolve into the
        // single-use guard rather than a live call.
        if (!active) { emitEnded('cancelled'); return; }
        sendJson({ type: 'end' });
      },

      /** Mute = stop sending frames client-side; the server is not involved. */
      setMuted: function (on) {
        muted = !!on;
      },

      /** Committed text (chip tap / typed): same path as speech turns. */
      sendText: function (text) {
        if (typeof text === 'string' && text.length > 0) {
          sendJson({ type: 'user_text', text: text });
        }
      },

      isMuted: function () { return muted; },
      isActive: function () { return active; },

      /**
       * Immediate local teardown (page hide etc.); server finalizes on close.
       * Its own reason: 'disconnected' is reserved for a socket that closed
       * on us, so consumers can tell a dropped call from one they tore down.
       */
      dispose: function () {
        emitEnded('disposed');
      },
    };

    return api;
  }

  global.SuzanohVoiceCall = { create: create, resolveWsUrl: resolveWsUrl, makeDownsampler: makeDownsampler };
})(typeof window !== 'undefined' ? window : this);
